import React, { useEffect, useMemo, useState } from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import { fetchUserManual } from '../../services/helpApi';
import renderManual from './renderManual';

/**
 * UserManualPage -- renders the static user manual served by the DM backend.
 *
 * Layout: a sticky table-of-contents sidebar (depth-2 section headings)
 * next to the rendered markdown. Anchors are derived from heading text;
 * clicking the TOC scrolls smoothly to the target.
 */
export default function UserManualPage() {
  usePageTitle('User manual');

  const [body, setBody] = useState('');
  const [sections, setSections] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // TOC opens by default on desktop, collapses on mobile to avoid
  // pushing the article off-screen. Updates live as the user resizes.
  const [tocOpen, setTocOpen] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(min-width: 881px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 881px)');
    const onChange = (e) => setTocOpen(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await fetchUserManual();
        if (cancelled) return;
        setBody(resp?.data?.markdown || '');
        setSections(resp?.data?.sections || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Render the markdown once per body change. renderManual is a tiny
  // inline renderer (see ./renderManual.js) that covers the subset
  // actually used in the manual; this avoids a node_modules addition
  // outside the shared cm-frontend-builder image.
  const html = useMemo(() => (body ? renderManual(body) : ''), [body]);

  function jumpTo(anchor) {
    const el = document.getElementById(anchor);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const filteredSections = useMemo(() => {
    if (!filter.trim()) return sections;
    const n = filter.trim().toLowerCase();
    return sections.filter((s) => s.title.toLowerCase().includes(n));
  }, [sections, filter]);

  return (
    <div className="dm-page dm-manual">
      <header className="dm-page__header">
        <h1 className="dm-page__title">User manual</h1>
        <p className="dm-page__lede">
          Everything shipped in the Denial Management product, by module and
          by persona. Use the table of contents to jump in, or open{' '}
          <a href="/help/chat">Ask CM</a> to query the same content
          conversationally across every denial-mitigation knowledge graph.
        </p>
        <div className="dm-page__actions">
          <a className="dm-btn dm-btn--secondary"
              href="/dm-api/help/manual" target="_blank" rel="noreferrer">
            Open raw markdown
          </a>
          <a className="dm-btn dm-btn--secondary" href="/help/chat">
            Ask CM
          </a>
        </div>
      </header>

      {error && <div className="dm-banner dm-banner--error">{error}</div>}
      {loading && <div className="dm-card__empty">Loading manual...</div>}

      {!loading && !error && (
        <div className="dm-manual__layout">
          <aside className="dm-manual__toc" aria-label="Table of contents">
            {/* <details> is the entire TOC. On desktop the disclosure
                marker is hidden via CSS and the panel is open; on narrow
                viewports the TOC starts collapsed so the article is
                immediately readable. The open state tracks a matchMedia
                listener so resizes update live. */}
            <details
              className="dm-manual__toc-details"
              open={tocOpen}
              onToggle={(e) => setTocOpen(e.currentTarget.open)}
            >
              <summary className="dm-manual__toc-summary">
                Table of contents
                {sections.length > 0 && (
                  <span className="dm-manual__toc-count">
                    {sections.length}
                  </span>
                )}
              </summary>
              <input
                type="search"
                className="dm-search"
                placeholder="Filter sections..."
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <ul>
                {filteredSections.map((s) => (
                  <li key={s.id}>
                    <button type="button" onClick={() => jumpTo(s.anchor)}>
                      {s.title}
                    </button>
                  </li>
                ))}
                {filteredSections.length === 0 && (
                  <li className="dm-manual__toc-empty">No sections match.</li>
                )}
              </ul>
            </details>
          </aside>

          <article
            className="dm-manual__content"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      )}
    </div>
  );
}
